import React, { useState, useCallback } from 'react';
import {
  SubmitHandler,
  Controller,
  useFormContext,
} from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  styled,
} from '@material-ui/core';

import { isValidPhoneNumber } from 'react-phone-number-input';

import { PreOnboardingParams, SetupForm } from '../PreOnboardingPage';
import { useEnrollDirectAccessPatient } from '../../../hooks/useEnrollDirectAccessPatient';

import ProgressBarStep from '@/components/Onboarding/ProgressBarStep/ProgressBarStep';
import VerticalListStep from '@/components/Onboarding/VerticalListStep/VerticalListStep';
import { getProductName, isSpark as isSparkHelper } from '@/utils/productHelpers';
import type { Product } from '@/utils/productHelpers';
import Button from '@/components/Button';
import { VARIANT_A, VARIANT_B } from '@/utils/constants';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';

const EmailHint = styled(Typography)(({ theme }) => ({
  fontFamily: 'GT Walsheim',
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: 0,
  color: theme.palette.grey[500],
}));

interface SubmitEmailProps {
  onNext: () => void;
  onError: () => void;
  isGeneric?: boolean;
}

const SubmitEmail = ({ onNext, onError, isGeneric = true }: SubmitEmailProps) => {
  const { t } = useTranslation();
  const { product } = useParams<PreOnboardingParams>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const variant = params.get('variant');
  const isSpark = isSparkHelper(product);
  const isSleepioVariantActive = !isSpark && [VARIANT_A, VARIANT_B].includes(variant ?? '');
  const productForTitle = (isSpark ? 'spark' : 'sleepio') as Product;
  const [returnError, setReturnError] = useState(false);
  const { enroll, loading } = useEnrollDirectAccessPatient();

  const {
    handleSubmit,
    control,
    formState,
  } = useFormContext<SetupForm>();

  const onSubmit: SubmitHandler<SetupForm> = useCallback(async (data) => {
    if (returnError) {
      console.error('Intentional error produced by the override toggle');
      onError();
      return;
    }

    try {
      const res = await enroll({
        email: data.email,
        phoneNumber: data.phoneNumber,
        marketingConsent: data.marketingConsent,
      });

      if (res.success) {
        onNext();
      } else {
        onError();
      }
    } catch (e) {
      console.warn('Error during enroll:', e);
      onError();
    }
  }, [enroll, onError, onNext, returnError]);

  const flowType = isGeneric ? 'generic' : 'specific';
  const welcomeTitle = isSleepioVariantActive
    ? `coverage.pre_onboarding.${flowType}.associated.sleepio`
    : `coverage.pre_onboarding.${flowType}.welcome`;
  const emailLabel = `coverage.pre_onboarding.${flowType}.email_label`;
  const emailPlaceholder = `coverage.pre_onboarding.${flowType}.email_placeholder`;
  const submitButtonText = `coverage.pre_onboarding.${flowType}.submit_email`;

  const getSubtitle = () => {
    if (isSleepioVariantActive) {
      return 'onboarding.variant_subtitle';
    }
    if (isSpark) {
      return 'coverage.spark.person_enrolling.subtitle_enroll';
    }
    return `coverage.pre_onboarding.sleepio.subtitle.${flowType}`;
  };
  const subtitle = getSubtitle();

  return (
    <>
      {isSleepioVariantActive && variant === VARIANT_A && <ProgressBarStep currentStep={1} />}
      <Typography
        variant="h2"
        style={{ marginTop: isSleepioVariantActive && variant === VARIANT_A ? 16 : 32 }}
        color="textPrimary"
      >
        {t(welcomeTitle, { product: getProductName(productForTitle) })}
      </Typography>
      <Typography style={{ marginTop: 8, marginBottom: 32 }} color="textPrimary">
        {t(subtitle, { productName: getProductName(productForTitle) })}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="email"
          render={({ field, fieldState }) => (
            <TextField
              id="email"
              data-testid="email-input"
              {...field}
              autoFocus
              variant="outlined"
              fullWidth
              required
              color="secondary"
              label={t(emailLabel)}
              placeholder={t(emailPlaceholder)}
              // forces label to always appear above input
              InputLabelProps={{
                shrink: true,
              }}
              error={fieldState.error?.message === 'coverage.errors.blocked_domain_error'}
              helperText={
                fieldState.error?.message === 'coverage.errors.blocked_domain_error'
                  ? t(fieldState.error.message)
                  : undefined
              }
            />
          )}
        />
        {isSleepioVariantActive && (
          <EmailHint style={{ marginTop: 16 }}>{t('onboarding.email_hint')}</EmailHint>
        )}
        <div style={{ marginTop: 16 }} />
        {isSpark && (
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: t('coverage.sleepio.patient.please_enter_valid_phone_number'),
              validate: (v) => isValidPhoneNumber(v || '')
                || t('coverage.sleepio.patient.please_enter_valid_phone_number'),
            }}
            render={({ field, fieldState }) => (
              <PhoneNumberInput
                id="phoneNumber"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('coverage.sleepio.patient.phone_label')}
                error={!!fieldState.error}
                helperText={
                  fieldState.error
                    ? t('coverage.sleepio.patient.please_enter_valid_phone_number')
                    : undefined
                }
                required
              />
            )}
          />
        )}
        <Controller
          name="termsAndPrivacy"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControlLabel
              style={{ ...(!isSleepioVariantActive && { marginTop: 24 }), marginBottom: 32, alignItems: 'flex-start' }}
              label={(
                <Typography>
                  {`${t('coverage.i_agree_to_the')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.terms_of_use.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {t('footer.terms_of_use.label')}
                  </Link>
                  {` ${t('coverage.and')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.privacy_notice.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {`${t('footer.privacy_notice.label')}.`}
                  </Link>
                </Typography>
              )}
              control={(
                <Checkbox {...field} />
              )}
            />
          )}
        />
        {isSpark && (
          <>
            <Controller
              name="agreeToReceiveTextMessages"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControlLabel
                  style={{ marginTop: 24, marginBottom: 16 }}
                  label={(
                    <Typography>
                      {t('coverage.spark.patient.agree_to_receive_text_messages_spark')}
                    </Typography>
                  )}
                  control={<Checkbox {...field} checked={!!field.value} />}
                />
              )}
            />
            <Controller
              name="marketingConsent"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  style={{ marginTop: 8, marginBottom: 32 }}
                  label={(
                    <Typography>
                      {t('solera.account.marketing_consent')}
                    </Typography>
                  )}
                  control={<Checkbox {...field} checked={!!field.value} />}
                />
              )}
            />
          </>
        )}
        <Button
          variant="contained"
          role="button"
          data-testid="submit-button"
          type="submit"
          fullWidth
          color="primary"
          disabled={!formState.isValid || loading}
          loading={loading}
          rounded
          // NOTE: We conditionally spread the style prop instead of passing style={{}}
          // because an empty style object overrides the Button's internal styles from
          // the "rounded" prop. By not passing style at all when !isSpark, the Button
          // keeps its default rounded styling.
          {...(isSpark && {
            style: {
              borderRadius: 100,
              ...(formState.isValid && !loading && {
                backgroundColor: '#077b65',
                color: '#FFFFFF',
              }),
            },
          })}
        >
          {t(isSleepioVariantActive ? 'coverage.pre_onboarding.organization.check_submit' : submitButtonText)}
        </Button>
      </form>
      {process.env.NODE_ENV !== 'production' && (
        <OverrideToggle
          userMessage="Simulate server error"
          override={setReturnError}
        />
      )}
      {isSleepioVariantActive && variant === VARIANT_B && <VerticalListStep currentStep={1} />}
    </>
  );
};

export default SubmitEmail;
